import type { ColumnDef } from '@tanstack/react-table'
import { Printer } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { ScanInput } from 'shared/components/scan-input'
import { Button } from 'shared/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from 'shared/components/ui/dialog'
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { formatDayTime } from 'shared/lib/utils/date'
import {
	HubBagClose,
	type HubBagCloseAnswer,
	type HubBagCloseRequest,
	type HubBagRow,
	type HubBagView,
} from './HubBagClose'
import { BagLabelPrint } from './BagLabel'
import { CompactTable, EmptyTableBody, Hint, SectionTitle } from 'shared/components/section'
import { ToneBanner } from 'shared/components/tone-banner'

/**
 * Sorting at the hub into bags, and closing them (spec §12, §26 view C; ADR-0025;
 * ticket 26). One scan per Parcel, no weight: the answer comes back large and in
 * its colour — «→ пункт» green; «на полку хаба» (with why, in words), «пункт
 * сменился: был A, теперь B» and a repeat yellow; a refusal red — so it reads from
 * across the room. Under it: what still waits to be sorted, by Point; the open bags
 * by Point, where «Закрыть мешок» opens the close in a dialog (`HubBagClose`), which
 * prints the label; and the closed bags waiting for their Trip, each with «Этикетка»
 * to print it again.
 * The colour is the server's; this screen only shows it.
 *
 * The calls are injected (`scan`, `listBags`, `loadBag`, `closeBag`, `print`) so
 * the behaviour is tested without a backend; they default to the real tRPC client
 * and the browser.
 */

export type HubSortAnswer = RouterOutputs['sorting']['Scan']
export type HubSortRequest = RouterInputs['sorting']['Scan']
type HubShelfReason = Extract<HubSortAnswer, { outcome: 'to_hub_shelf' }>['reasons'][number]

type HubBagStatus = HubBagRow['status']

const scanViaTrpc = (input: HubSortRequest) => trpcClient.sorting.Scan.mutate(input)
const listViaTrpc = (status: HubBagStatus) => trpcClient.sorting.Bags.query({ status })
const loadBagViaTrpc = (code: string) => trpcClient.sorting.Bag.query({ code })
const unsortedViaTrpc = () => trpcClient.sorting.Unsorted.query()

/** What the hub holds and has not sorted yet, by Point (ticket 45). */
export type HubUnsortedRow = RouterOutputs['sorting']['Unsorted'][number]

const failure = (error: unknown) =>
	error instanceof Error ? error.message : String(error)

export interface HubSortingProps {
	/** Test seam: one sorting scan. */
	scan?: (input: HubSortRequest) => Promise<HubSortAnswer>
	/** Test seam: the bags on the rack (`open`) or closed and waiting for a Trip (`sealed`). */
	listBags?: (status: HubBagStatus) => Promise<HubBagRow[]>
	/** Test seam: a bag with its parcels (closing ribbon, reprinting its label). */
	loadBag?: (code: string) => Promise<HubBagView>
	/** Test seam: the close. */
	closeBag?: (input: HubBagCloseRequest) => Promise<HubBagCloseAnswer>
	/** Test seam: the browser's print of a label. */
	print?: () => void
	/** Test seam: what the hub holds and has not sorted yet, by Point (ticket 45). */
	listUnsorted?: () => Promise<HubUnsortedRow[]>
}

export function HubSorting({
	scan = scanViaTrpc,
	listBags = listViaTrpc,
	loadBag = loadBagViaTrpc,
	closeBag,
	print,
	listUnsorted = unsortedViaTrpc,
}: HubSortingProps) {
	const { t } = useTranslate()
	const [bags, setBags] = useState<HubBagRow[] | null>(null)
	const [unsorted, setUnsorted] = useState<HubUnsortedRow[]>([])
	const [sealedBags, setSealedBags] = useState<HubBagRow[]>([])
	const [bagsError, setBagsError] = useState<string | null>(null)
	const [reprint, setReprint] = useState<HubBagView | null>(null)
	const [reprintError, setReprintError] = useState<string | null>(null)
	const [answer, setAnswer] = useState<HubSortAnswer | null>(null)
	const [sendError, setSendError] = useState<string | null>(null)
	const [sending, setSending] = useState(false)
	const [closing, setClosing] = useState<HubBagRow | null>(null)
	// Bumped after each answer so the scan field remounts and takes the cursor back.
	const [scanRound, setScanRound] = useState(0)
	const reads = useRef(0)

	// The list is the server's: re-read after every act rather than rebuilt here.
	const refresh = useCallback(() => {
		const read = ++reads.current
		Promise.all([listBags('open'), listBags('sealed')]).then(
			([open, sealed]) => {
				if (read !== reads.current) return
				setBags(open)
				setSealedBags(sealed)
				setBagsError(null)
			},
			(error) => {
				if (read === reads.current) setBagsError(failure(error))
			},
		)
		// The count beside the bags: a failed read leaves the last one, the bags work on.
		listUnsorted().then(
			(waiting) => {
				if (read === reads.current) setUnsorted(waiting)
			},
			() => {},
		)
	}, [listBags, listUnsorted])

	useEffect(() => {
		refresh()
	}, [refresh])

	const onScan = async (code: string) => {
		if (sending) return
		setSending(true)
		setSendError(null)
		try {
			setAnswer(await scan({ code }))
			setScanRound((n) => n + 1)
			refresh()
		} catch (error) {
			setSendError(failure(error))
		} finally {
			setSending(false)
		}
	}

	const onReprint = async (code: string) => {
		setReprintError(null)
		try {
			setReprint(await loadBag(code))
		} catch (error) {
			setReprintError(failure(error))
		}
	}

	// The open bags, grouped by the Point they go to.
	const byPoint = new Map<string, { code: string; bags: HubBagRow[] }>()
	for (const bag of bags ?? []) {
		const group = byPoint.get(bag.pointId) ?? { code: bag.pointCode, bags: [] }
		group.bags.push(bag)
		byPoint.set(bag.pointId, group)
	}

	return (
		<div className='flex flex-col gap-8' data-testid='hub-sorting'>
			<section className='flex flex-col gap-3'>
				<ScanInput key={scanRound} onScan={onScan} disabled={sending || closing !== null} />
				{sendError && (
					<p role='alert' className='text-sm text-destructive' data-testid='hub-sort-error'>
						{t('hubSorting.scanFailed', { message: sendError })}
					</p>
				)}
				{answer ? (
					<SortAnswerBanner answer={answer} />
				) : (
					<p className='text-xs text-muted-foreground'>{t('hubSorting.scanHint')}</p>
				)}
			</section>

			<UnsortedCount rows={unsorted} />

			<div className='grid items-start gap-8 lg:grid-cols-2'>
				<section className='flex flex-col gap-2'>
					<SectionTitle title={t('hubSorting.bagsTitle')} count={bags?.length} />
					{bagsError ? (
						<p className='text-sm text-destructive' data-testid='hub-bags-error'>
							{t('hubSorting.bagsLoadFailed', { message: bagsError })}
						</p>
					) : bags === null ? (
						<p className='text-sm text-muted-foreground'>{t('hub.loading')}</p>
					) : (
						<CompactTable>
							<TableHeader>
								<TableRow>
									<TableHead>{t('hub.shelfPoint')}</TableHead>
									<TableHead>{t('hubSorting.colBag')}</TableHead>
									<TableHead className='text-right'>{t('hubSorting.colParcels')}</TableHead>
									<TableHead />
								</TableRow>
							</TableHeader>
							{bags.length === 0 && (
								<EmptyTableBody columns={4}>
									<span data-testid='hub-bags-empty'>{t('hubSorting.bagsEmpty')}</span>
								</EmptyTableBody>
							)}
							{[...byPoint.entries()].map(([pointId, group]) => (
								<TableBody
									key={pointId}
									className='border-b last:border-b-0'
									data-testid={`hub-bags-point-${pointId}`}
								>
									{group.bags.map((bag, i) => (
										<TableRow
											key={bag.containerId}
											data-testid={`hub-bag-${bag.code}`}
											data-count={bag.parcelCount}
										>
											<TableCell className='align-top text-base font-semibold'>
												{i === 0 ? group.code : ''}
											</TableCell>
											<TableCell className='align-top'>
												<div className='font-mono'>{bag.code}</div>
												<BagPointClosure bag={bag} />
											</TableCell>
											<TableCell
												className='text-right align-top tabular-nums'
												data-testid={`hub-bag-count-${bag.code}`}
											>
												{bag.parcelCount}
											</TableCell>
											<TableCell className='w-px text-right align-top'>
												<Button
													size='sm'
													variant='outline'
													data-testid={`hub-bag-close-${bag.code}`}
													disabled={closing !== null}
													onClick={() => setClosing(bag)}
												>
													{t('hubSorting.closeBag')}
												</Button>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							))}
						</CompactTable>
					)}
				</section>

				<SealedBags bags={sealedBags} onReprint={onReprint} />
			</div>

			{reprintError && (
				<p role='alert' className='text-sm text-destructive' data-testid='hub-bag-reprint-error'>
					{t('hubSorting.reprintFailed', { message: reprintError })}
				</p>
			)}

			{closing && (
				<HubBagClose
					key={closing.code}
					bag={closing}
					loadBag={loadBag}
					closeBag={closeBag}
					print={print}
					onSealed={refresh}
					onDone={() => {
						setClosing(null)
						refresh()
						setScanRound((n) => n + 1)
					}}
				/>
			)}

			<Dialog open={reprint !== null} onOpenChange={(open) => !open && setReprint(null)}>
				<DialogContent className='sm:max-w-md' aria-describedby={undefined} data-testid='hub-bag-reprint'>
					<DialogHeader>
						<DialogTitle>{t('hubSorting.reprintTitle')}</DialogTitle>
					</DialogHeader>
					{reprint && <BagLabelPrint key={reprint.code} bag={reprint} printNow print={print} />}
					<DialogFooter>
						<Button
							type='button'
							variant='outline'
							data-testid='hub-bag-reprint-close'
							onClick={() => setReprint(null)}
						>
							{t('hubSorting.reprintClose')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	)
}

/** The closed bags waiting for their Trip, each with its label to print again. */
function SealedBags({ bags, onReprint }: { bags: HubBagRow[]; onReprint: (code: string) => void }) {
	const { t } = useTranslate()
	const columns = useMemo<ColumnDef<HubBagRow>[]>(
		() => [
			{
				id: 'bag',
				header: t('hubSorting.colBag'),
				cell: ({ row: { original: bag } }) => (
					<div>
						<div className='font-mono'>{bag.code}</div>
						<BagPointClosure bag={bag} />
					</div>
				),
			},
			{
				id: 'point',
				header: t('hub.shelfPoint'),
				cell: ({ row }) => <span className='font-semibold'>→ {row.original.pointCode}</span>,
			},
			{
				id: 'parcels',
				header: t('hubSorting.colParcels'),
				meta: { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' },
				cell: ({ row }) => row.original.parcelCount,
			},
			{
				id: 'sealedAt',
				header: t('hubSorting.colSealedAt'),
				cell: ({ row }) => (
					<span className='text-muted-foreground'>
						{row.original.sealedAt ? formatDayTime(row.original.sealedAt) : '—'}
					</span>
				),
			},
			{
				id: 'actions',
				header: '',
				meta: { cellClassName: 'w-px text-right' },
				cell: ({ row }) => (
					<Button
						size='sm'
						variant='ghost'
						data-testid={`hub-bag-label-${row.original.code}`}
						onClick={() => onReprint(row.original.code)}
					>
						<Printer />
						{t('hubSorting.reprint')}
					</Button>
				),
			},
		],
		[t, onReprint],
	)
	const { table } = useDataTable({
		data: bags,
		columns,
		rowCount: bags.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		getRowId: (bag) => bag.containerId,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})
	return (
		<section className='flex flex-col gap-2'>
			<SectionTitle title={t('hubSorting.sealedBagsTitle')} count={bags.length} />
			<DataTable
				table={table}
				withPagination={false}
				maxHeightClassName='max-h-[480px]'
				emptyText={<span data-testid='hub-sealed-bags-empty'>{t('hubSorting.sealedBagsEmpty')}</span>}
				row={{ attributes: (bag) => ({ 'data-testid': `hub-sealed-bag-${bag.code}` }) }}
			/>
		</section>
	)
}

/** The words of one answer — its headline and its detail — by `outcome`. */
function useAnswerWords() {
	const { t } = useTranslate()
	return (answer: HubSortAnswer): { title: string; detail: string } => {
		switch (answer.outcome) {
			case 'sorted':
				return {
					title: t('hubSorting.answerSorted', { point: answer.pointCode }),
					detail:
						t('hubSorting.answerSortedDetail', {
							code: answer.trackingCode,
							bag: answer.bag.code,
							count: String(answer.bag.parcelCount),
						}) + (answer.bag.created ? t('hubSorting.answerNewBag') : ''),
				}
			case 'point_changed':
				return {
					title: t('hubSorting.answerSorted', { point: answer.pointCode }),
					detail:
						t('hubSorting.answerPointChanged', {
							was: answer.previousPointCode,
							now: answer.pointCode,
						}) +
						' · ' +
						t('hubSorting.answerSortedDetail', {
							code: answer.trackingCode,
							bag: answer.bag.code,
							count: String(answer.bag.parcelCount),
						}) +
						(answer.bag.created ? t('hubSorting.answerNewBag') : ''),
				}
			case 'to_hub_shelf':
				return {
					title: t('hubSorting.answerToShelf'),
					detail: t('hubSorting.answerToShelfDetail', {
						code: answer.trackingCode,
						reasons: answer.reasons
							.map((r: HubShelfReason) => t(`hubShelfReason.${r}`) || r)
							.join(', '),
					}),
				}
			case 'found':
				return {
					title: t('hubFound.title'),
					detail: t('hubFound.detail', { code: answer.trackingCode, partner: answer.partnerName, point: answer.pointCode }),
				}
			case 'already_in_bag':
				return {
					title: t('hubSorting.answerRepeat'),
					detail: t('hubSorting.answerRepeatDetail', {
						code: answer.trackingCode,
						bag: answer.bag.code,
						point: answer.pointCode,
					}),
				}
			case 'box_rides_alone':
				return {
					title: t('hubSorting.answerBoxRidesAlone', { point: answer.pointCode }),
					detail: t('hubSorting.answerBoxRidesAloneDetail', {
						box: answer.box.code,
						parcels: String(answer.box.parcelCount),
						point: answer.pointCode,
					}),
				}
			case 'box_to_hub_shelf':
				return {
					title: t('hubSorting.answerBoxToShelf'),
					detail: t('hubSorting.answerBoxToShelfDetail', {
						box: answer.box.code,
						parcels: String(answer.box.parcelCount),
						point: answer.pointCode,
						reasons: answer.reasons
							.map((r: HubShelfReason) => t(`hubShelfReason.${r}`) || r)
							.join(', '),
					}),
				}
			case 'refused':
				return {
					title: t('hubSorting.answerRefused'),
					detail:
						t('hubSorting.answerRefusedDetail', {
							code: answer.trackingCode,
							reason: t(`hubSortRefusal.${answer.reason}`) || answer.reason,
						}) + (answer.boxCode ? ` — ${t('hubBox.inBoxNamed', { box: answer.boxCode })}` : ''),
				}
		}
	}
}

/** The answer to the last scan: large, in its colour, readable from afar. */
function SortAnswerBanner({ answer }: { answer: HubSortAnswer }) {
	const words = useAnswerWords()(answer)
	return (
		<ToneBanner
			tone={answer.tone}
			title={words.title}
			detail={words.detail}
			testId='hub-sort-answer'
			titleTestId='hub-sort-answer-title'
			detailTestId='hub-sort-answer-detail'
			attributes={{ 'data-outcome': answer.outcome }}
		/>
	)
}

/** A Bag whose Point is closed (ticket 45): it waits at the hub, or its Parcels go elsewhere. */
function BagPointClosure({ bag }: { bag: HubBagRow }) {
	const { t } = useTranslate()
	if (!bag.pointClosure) return null
	return (
		<div
			className='text-xs font-medium text-amber-700 dark:text-amber-400'
			data-testid={`hub-bag-closed-${bag.code}`}
			data-closure={bag.pointClosure}
		>
			{bag.pointClosure === 'temporarily' ? t('hubSorting.bagPointClosed') : t('hubSorting.bagPointClosedForGood')}
		</div>
	)
}

/**
 * «В хабе не рассортировано» (ticket 45, decision 7): what the hub holds by Point with
 * nothing in its way — so what waited through a Point's closure is not forgotten
 * once it opens. The server counts; this shows.
 */
function UnsortedCount({ rows }: { rows: HubUnsortedRow[] }) {
	const { t } = useTranslate()
	return (
		<section className='flex flex-wrap items-center gap-2' data-testid='hub-unsorted'>
			<div className='mr-2 flex items-center gap-2'>
				<h3 className='text-sm font-semibold'>{t('hubSorting.unsortedTitle')}</h3>
				<Hint>{t('hubSorting.unsortedHint')}</Hint>
			</div>
			{rows.length === 0 ? (
				<span className='text-sm text-muted-foreground' data-testid='hub-unsorted-none'>
					{t('hubSorting.unsortedNone')}
				</span>
			) : (
				rows.map((r) => (
					<span
						key={r.pointId}
						className='inline-flex items-center gap-2 rounded-md border px-2.5 py-1 text-sm'
						data-testid={`hub-unsorted-${r.pointId}`}
					>
						<span className='font-semibold'>{r.pointCode}</span>
						<span className='text-muted-foreground'>
							{[
								r.parcels > 0 ? t('hubSorting.unsortedParcels', { count: String(r.parcels) }) : null,
								r.boxes > 0 ? t('hubSorting.unsortedBoxes', { count: String(r.boxes) }) : null,
							]
								.filter(Boolean)
								.join(' · ')}
						</span>
					</span>
				))
			)}
		</section>
	)
}
